/**
 * RBAC 6-Rollen-Modell (Spec 0042)
 *
 * Platform-Rollen (kein Tenant-Scope):
 * - platform_admin:  Merchant-CRUD, Business-Ueberblick
 * - platform_it:     IQ-Service-CRUD, technisches Debugging
 * - platform_ops:    Monitoring, Subscription-Cancel
 *
 * Merchant-Rollen (Tenant-gebunden):
 * - merchant_product_manager:  Service-CRUD, Themes, Legal Links
 * - merchant_it:               Webhooks, TX-Trigger, Integration
 * - merchant_viewer:           Read-only
 */
export type UserRole =
  | 'merchant_product_manager'
  | 'merchant_it'
  | 'merchant_viewer'
  | 'platform_admin'
  | 'platform_it'
  | 'platform_ops';

// Rollen-Gruppen fuer Guards und Navigation
export const PLATFORM_ROLES: UserRole[] = [
  'platform_admin', 'platform_it', 'platform_ops',
];
export const MERCHANT_ROLES: UserRole[] = [
  'merchant_product_manager', 'merchant_it', 'merchant_viewer',
];

export function isPlatformRole(role: UserRole): boolean {
  return PLATFORM_ROLES.includes(role);
}
export function isMerchantRole(role: UserRole): boolean {
  return MERCHANT_ROLES.includes(role);
}

/**
 * Role-Groups fuer schnelle Zuweisungen in Route-Protection und UI-Visibility.
 * Jede Gruppe bildet ein Berechtigungskonzept ab.
 */
export const ROLE_GROUPS = {
  ALL: [...PLATFORM_ROLES, ...MERCHANT_ROLES] as UserRole[],
  PLATFORM: PLATFORM_ROLES,
  MERCHANT: MERCHANT_ROLES,
  SERVICE_WRITE: ['platform_admin', 'merchant_product_manager'] as UserRole[],
  // Spec 516 D-2: Journey-Host (publicBaseUrl) ist platform-only schreibbar —
  // die Facade validiert den Wert nicht, ein Fehlwert = tote Journey; die
  // Alias-Provisionierung laeuft ohnehin ueber das Facade-Team. PM behaelt
  // das uebrige Service-Form (SERVICE_WRITE unveraendert); der BFF erzwingt
  // dieselbe Grenze serverseitig (403 bei publicBaseUrl-Key von Nicht-Platform).
  SERVICE_JOURNEY_HOST_WRITE: ['platform_admin'] as UserRole[],
  TECH: ['platform_it', 'merchant_it'] as UserRole[],
  OPS_AND_ADMIN: ['platform_admin', 'platform_ops'] as UserRole[],
  // Issue #407: merchant_product_manager ist voller Self-Service-Merchant-Admin fuer den
  // eigenen Tenant (Backend-Rechte webhook-events:read:own + webhook-config:*:own) — sieht
  // daher die Webhook-Ereignisse, kann die eigene Webhook-Config lesen/aendern/rotieren/testen.
  WEBHOOK_EVENTS_READ: [
    'platform_admin', 'platform_it', 'platform_ops', 'merchant_it', 'merchant_product_manager',
  ] as UserRole[],
  WEBHOOK_CONFIG_WRITE: ['platform_admin', 'merchant_it', 'merchant_product_manager'] as UserRole[],
  WEBHOOK_CONFIG_ACCESS: [
    'platform_admin', 'platform_it', 'merchant_it', 'merchant_product_manager',
  ] as UserRole[],
  // Issue #404: merchant_product_manager darf Test-Transaktionen fuer den eigenen
  // Merchant ausloesen (Backend-Recht transactions:trigger:own). platform_ops und
  // merchant_viewer haben kein Trigger-Recht und sehen die Affordanz nicht.
  TX_TRIGGER: ['platform_admin', 'platform_it', 'merchant_it', 'merchant_product_manager'] as UserRole[],
  // Issue #407: PM darf eigene Abos kuendigen (Backend-Recht subscriptions:cancel:own).
  SUB_CANCEL: ['platform_admin', 'platform_ops', 'merchant_it', 'merchant_product_manager'] as UserRole[],
  IQ_SERVICES_ACCESS: ['platform_admin', 'platform_it'] as UserRole[],
  // platform_admin laut Cognito-Group-Description "Full system access - all
  // merchants and IQ config" — Permission-Matrix angeglichen 2026-05-20.
  // Spec 0042 hatte ursprueglich platform_it-Exklusivitaet, das wurde via
  // Cognito-Setup (nur 2 Groups: platform_admin + merchant_admin) ohnehin
  // unterlaufen.
  IQ_SERVICES_CRUD: ['platform_admin', 'platform_it'] as UserRole[],
  MERCHANT_CRUD: ['platform_admin'] as UserRole[],
  // Spec 0221: User-Management — platform_admin (alle) + merchant_product_manager (eigenes Team).
  // Bewusst NICHT MERCHANT_CRUD wiederverwenden (das gated auch /merchants/new).
  USER_MANAGEMENT: ['platform_admin', 'merchant_product_manager'] as UserRole[],
  // Spec 525: Lese-Zugang zur /users-LISTE (Presence-Spalten). platform_ops kommt
  // hinzu (list-only, read); /users/new + /users/:sub bleiben auf USER_MANAGEMENT.
  // Der Server erzwingt list-only fuer platform_ops zusaetzlich (assertUserDetailAccess).
  USER_LIST_VIEW: ['platform_admin', 'platform_ops', 'merchant_product_manager'] as UserRole[],
  // Issue #318: MNO-IP-Ranges. Platform-Verwaltung sichtbar fuer platform_admin +
  // platform_it (CRUD nur platform_admin); PM-Lese-Ansicht zusaetzlich fuer
  // merchant_product_manager.
  MNO_IP_RANGES_ACCESS: ['platform_admin', 'platform_it'] as UserRole[],
  MNO_IP_RANGES_CRUD: ['platform_admin'] as UserRole[],
  MNO_IP_RANGES_READ: [
    'platform_admin', 'platform_it', 'platform_ops', 'merchant_product_manager',
  ] as UserRole[],
  // Issue #334: Merchant-Asset-Bibliothek. Cross-Pin zu den BFF-Gates in
  // src/services/merchant-asset/merchant-asset-roles.ts (Pin-Test:
  // frontend/src/types/__tests__/merchant-asset-roles.test.ts).
  // READ: alle sechs Rollen (Merchant-Rollen sind im BFF auf den eigenen Merchant
  // gescoped). WRITE: alle ausser merchant_viewer.
  MERCHANT_ASSET_READ: [...PLATFORM_ROLES, ...MERCHANT_ROLES] as UserRole[],
  MERCHANT_ASSET_WRITE: [
    'platform_admin', 'platform_it', 'platform_ops',
    'merchant_product_manager', 'merchant_it',
  ] as UserRole[],
  // Spec 344: IQ-Service-Inventory (merchant-nested). Owner-Entscheidung 2026-06-19:
  // Verwaltung platform-only (ACCESS = Seiten-Sichtbarkeit, CRUD = Writes — beide
  // platform_admin + platform_it). READ ergaenzt platform_ops + merchant_product_manager
  // (im BFF auf den eigenen Merchant gescoped) fuer das UC-5-Wizard-Dropdown. Cross-Pin
  // zu den BFF-Gates in src/services/iq-service-inventory/iq-service-inventory-roles.ts
  // (Backend-Pin-Test importiert diese Konstanten; Frontend-Pin-Test:
  // frontend/src/types/__tests__/iq-service-inventory-roles.test.ts).
  IQ_SERVICE_INVENTORY_ACCESS: ['platform_admin', 'platform_it'] as UserRole[],
  IQ_SERVICE_INVENTORY_CRUD: ['platform_admin', 'platform_it'] as UserRole[],
  IQ_SERVICE_INVENTORY_READ: [
    'platform_admin', 'platform_it', 'platform_ops', 'merchant_product_manager',
  ] as UserRole[],
  // Spec 347 §9: Cross-Merchant-Uebersicht — platform-only (= PLATFORM_ROLES).
  // Bewusst NICHT _READ wiederverwenden: das schliesst den tenant-gescopten
  // merchant_product_manager ein, der tenant-uebergreifende Daten nicht sehen darf.
  IQ_SERVICE_INVENTORY_OVERVIEW: [...PLATFORM_ROLES] as UserRole[],
  // Spec 429 Increment D: Analytics-Reports (QuickSight-Embed). Nav-/Route-Sichtbarkeit fuer
  // alle sechs Rollen (Owner-Entscheid Platform + Merchant) — spiegelt das Backend-Recht
  // reports:read / reports:read:own (src/types/auth.ts). Die feingranulare reportKey-Isolation
  // (Platform-Reports nur Platform-Rollen, Merchant-Report group-RLS) erzwingt der Mint-Handler
  // serverseitig via isEntitled; diese Gruppe steuert nur die grobe Feature-Sichtbarkeit.
  REPORTS_READ: [...PLATFORM_ROLES, ...MERCHANT_ROLES] as UserRole[],
  // Spec 493: Payment-Link Verwaltung — platform-only (hoheitliche Provisionierung). Steuert
  // Nav-/Route-Sichtbarkeit; das Backend-Recht `payment-link-provisioning:read` (nur Platform-
  // Rollen) erzwingt die BFF-Route zusaetzlich serverseitig. In Inc 1 sehen alle drei Platform-
  // Rollen dieselbe read-only Flaeche (platform_ops == platform_admin, da nur Lesen existiert).
  PAYMENT_LINK_READ: [...PLATFORM_ROLES] as UserRole[],
  // Spec 497 Inc 2: Mutations-Affordanzen (aktivieren/deaktivieren, Salt rotieren) nur fuer
  // platform_admin/platform_it — spiegelt die Backend-Rechte enable/disable/rotate-secret.
  // platform_ops ist bewusst NICHT enthalten (nur Lesen). Das FE-Gate blendet die Affordanzen
  // aus; das BFF-Gate (authorizeOperation) erzwingt es zusaetzlich serverseitig.
  PAYMENT_LINK_WRITE: ['platform_admin', 'platform_it'] as UserRole[],
  // Spec 510: service-zentrierter Payment-Link-Einstieg (/services/:id/payment-link inkl.
  // service-actions-Dropdown + Detail-Button) — Spiegel der Backend-:own-Rechte von PM +
  // merchant_it (payment-link-provisioning:*:own, Tenant-Grenze erzwingt der BFF serverseitig).
  // platform_ops + merchant_viewer bewusst NICHT enthalten (reiner Write-Flow, Sackgassen-
  // Vermeidung). PAYMENT_LINK_READ/WRITE bleiben unveraendert die Gates der platform-only
  // Betriebsflaeche /payment-link (Sidebar, Drawer, Tester-Seite).
  PAYMENT_LINK_SERVICE_ACCESS: [
    'platform_admin', 'platform_it', 'merchant_product_manager', 'merchant_it',
  ] as UserRole[],
  // Spec 528: Audit-Trail-Plattform-Sicht. READ = Nav-/Route-Sichtbarkeit
  // (platform_admin + platform_ops, spiegelt das Backend-Recht `audit:read`);
  // platform_it bewusst NICHT (kein audit:read). EXPORT = Export-Affordanz nur
  // platform_admin (`audit:export`). Das BFF erzwingt beide serverseitig via
  // authorizeOperation.
  AUDIT_READ: ['platform_admin', 'platform_ops'] as UserRole[],
  AUDIT_EXPORT: ['platform_admin'] as UserRole[],
} as const;

/**
 * Authenticated user information extracted from Cognito tokens.
 */
export interface AuthUser {
  /** Unique user identifier (Cognito sub) */
  userId: string;
  /** User's email address */
  email: string;
  /** User's role determining access levels */
  role: UserRole;
  /** Associated merchant ID (null for platform roles) */
  merchantId: string | null;
  /** Whether user has completed MFA setup (Spec 0045) */
  mfaSetupComplete: boolean;
}

/**
 * Auth state for the application.
 */
export interface AuthState {
  /** Current authenticated user, null if not logged in */
  user: AuthUser | null;
  /** Whether auth state is still being determined */
  isLoading: boolean;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Current access token for API calls */
  accessToken: string | null;
}
